import { useCallback, useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const SCRIPT_ID = "recaptcha-v3";

/** Load the reCAPTCHA v3 script once per page, shared across callers. */
let loaderPromise = null;

const loadScript = () => {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");

    script.addEventListener("load", () => resolve(window.grecaptcha));
    script.addEventListener("error", () => {
      // Allow a later attempt to retry rather than caching the failure forever.
      loaderPromise = null;
      reject(new Error("Failed to load reCAPTCHA"));
    });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loaderPromise;
};

/**
 * reCAPTCHA v3 (invisible, score-based).
 *
 * Returns { enabled, execute }. When REACT_APP_RECAPTCHA_SITE_KEY is unset the
 * hook is inert and execute() resolves to null, so local development and any
 * deploy without keys still works — the backend logs a warning in that case.
 */
export const useRecaptcha = (action = "lead_submit") => {
  const enabled = Boolean(SITE_KEY);
  const readyRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Warm the script up front so the first submit is not delayed by the fetch.
    loadScript()
      .then(() => {
        if (!cancelled) readyRef.current = true;
      })
      .catch(() => {
        /* execute() surfaces the failure at submit time */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const execute = useCallback(async () => {
    if (!enabled) return null;

    const grecaptcha = await loadScript();
    return new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(SITE_KEY, { action })
          .then(resolve)
          .catch(reject);
      });
    });
  }, [enabled, action]);

  return { enabled, execute };
};

export default useRecaptcha;
